import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, ArrowBack, Delete, Save} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {
    CreateXmlExampleModel,
    GetXmlExampleData,
    UpdateXmlExampleModel,
    XmlExampleStatus,
    XmlSnippetDto,
} from 'apiClient/Api';

interface SnippetDraft {
    /** Server-assigned id for known snippets, or `local-N` for unsaved ones. */
    localKey: string;
    /** undefined when the snippet has never been persisted. */
    serverId: string | null | undefined;
    title: string;
    description: string;
    xmlContent: string;
}

interface FormState {
    title: string;
    description: string;
    features: string[];
    targetVersion: string;
    status: XmlExampleStatus;
    notes: string;
    snippets: SnippetDraft[];
}

const emptyForm: FormState = {
    title: '',
    description: '',
    features: [],
    targetVersion: '',
    status: XmlExampleStatus.Draft,
    notes: '',
    snippets: [],
};

const newLocalKey = (() => {
    let counter = 0;
    return () => `local-${++counter}`;
})();

const toForm = (data: GetXmlExampleData): FormState => ({
    title: data.title ?? '',
    description: data.description ?? '',
    features: data.features ?? [],
    targetVersion: data.targetVersion ?? '',
    status: data.status ?? XmlExampleStatus.Draft,
    notes: data.notes ?? '',
    snippets: (data.snippets ?? []).map(s => ({
        localKey: s.id ?? newLocalKey(),
        serverId: s.id ?? null,
        title: s.title,
        description: s.description,
        xmlContent: s.xmlContent,
    })),
});

const toApiSnippets = (snippets: SnippetDraft[]): XmlSnippetDto[] =>
    snippets.map(s => ({
        id: s.serverId ?? null,
        title: s.title,
        description: s.description,
        xmlContent: s.xmlContent,
    }));

/**
 * Editor for a single XML example with multiple Monaco-edited snippets.
 *
 * Routes:
 *   /admin/xml-examples/new   — create (no id)
 *   /admin/xml-examples/:id   — edit
 *
 * On save the server returns the persisted shape (with snippet ids); the form
 * is rehydrated so subsequent saves are stable PUTs that preserve snippet
 * identities (and therefore download links).
 */
const XmlExampleEdit: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('xmlExamples.edit.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {id} = useParams<{id?: string}>();
    const isNew = !id || id === 'new';

    const [form, setForm] = useState<FormState>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (isNew) {
            setForm(emptyForm);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const response = await context.api.xmlExample.xmlExampleGet(id!);
            setForm(toForm(response.data));
        } catch (e: any) {
            setLoadError(t('xmlExamples.edit.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.xmlExample, id, isNew, t]);

    useEffect(() => {
        load();
    }, [load]);

    const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({...prev, [key]: value}));
    };

    const updateSnippet = (localKey: string, patch: Partial<SnippetDraft>) => {
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.map(s => s.localKey === localKey ? {...s, ...patch} : s),
        }));
    };

    const addSnippet = () => {
        setForm(prev => ({
            ...prev,
            snippets: [
                ...prev.snippets,
                {
                    localKey: newLocalKey(),
                    serverId: null,
                    title: '',
                    description: '',
                    xmlContent: '',
                },
            ],
        }));
    };

    const removeSnippet = (localKey: string) => {
        setForm(prev => ({
            ...prev,
            snippets: prev.snippets.filter(s => s.localKey !== localKey),
        }));
    };

    const validate = (): string | null => {
        if (!form.title.trim()) return t('xmlExamples.edit.validation.titleRequired');
        if (!form.description.trim()) return t('xmlExamples.edit.validation.descriptionRequired');
        for (const s of form.snippets) {
            if (!s.title.trim()) return t('xmlExamples.edit.validation.snippetTitleRequired');
            if (!s.description.trim()) return t('xmlExamples.edit.validation.snippetDescriptionRequired');
            if (!s.xmlContent.trim()) return t('xmlExamples.edit.validation.snippetXmlRequired');
        }
        return null;
    };

    const save = async () => {
        const err = validate();
        if (err) {
            setValidationError(err);
            return;
        }
        setValidationError(null);
        setSaving(true);
        try {
            const payload: CreateXmlExampleModel | UpdateXmlExampleModel = {
                title: form.title.trim(),
                description: form.description.trim(),
                features: form.features,
                targetVersion: form.targetVersion.trim() || null,
                status: form.status,
                notes: form.notes.trim() || null,
                snippets: toApiSnippets(form.snippets),
            };
            const response = isNew
                ? await context.api.xmlExample.xmlExampleCreate(payload)
                : await context.api.xmlExample.xmlExampleUpdate(id!, payload);
            setForm(toForm(response.data));
            enqueueSnackbar(t('xmlExamples.edit.saved'), {variant: 'success'});
            if (isNew && response.data.id) {
                navigate(`/admin/xml-examples/${response.data.id}`, {replace: true});
            }
        } catch (e: any) {
            const msg = e?.response?.data?.detail ?? e?.message ?? t('xmlExamples.edit.saveError');
            enqueueSnackbar(msg, {variant: 'error'});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <Tooltip title={t('xmlExamples.edit.back')}>
                        <IconButton onClick={() => navigate('/admin/xml-examples')}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" component="h1">
                            {isNew ? t('xmlExamples.edit.newTitle') : t('xmlExamples.edit.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('xmlExamples.edit.subtitle')}
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={16}/> : <Save/>}
                    disabled={saving || loading}
                    onClick={save}
                >
                    {t('xmlExamples.edit.save')}
                </Button>
            </Stack>

            {loadError && <Alert severity="error" sx={{mb: 2}}>{loadError}</Alert>}
            {validationError && <Alert severity="warning" sx={{mb: 2}}>{validationError}</Alert>}

            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Stack spacing={3}>
                    {/* Metadata card */}
                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="h6">{t('xmlExamples.edit.metadata')}</Typography>
                                <TextField
                                    label={t('xmlExamples.fields.title')}
                                    value={form.title}
                                    onChange={(e) => updateField('title', e.target.value)}
                                    required
                                    fullWidth
                                    slotProps={{htmlInput: {maxLength: 200}}}
                                />
                                <TextField
                                    label={t('xmlExamples.fields.description')}
                                    value={form.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    required
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('xmlExamples.fields.descriptionHelper')}
                                />
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[] as string[]}
                                        value={form.features}
                                        onChange={(_, value) => updateField('features', value as string[])}
                                        sx={{flex: 2}}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('xmlExamples.fields.features')}
                                                placeholder={t('xmlExamples.fields.featuresPlaceholder')}
                                                helperText={t('xmlExamples.fields.featuresHelper')}
                                            />
                                        )}
                                    />
                                    <TextField
                                        label={t('xmlExamples.fields.targetVersion')}
                                        value={form.targetVersion}
                                        onChange={(e) => updateField('targetVersion', e.target.value)}
                                        sx={{flex: 1}}
                                        slotProps={{htmlInput: {maxLength: 50}}}
                                    />
                                    <FormControl sx={{flex: 1, minWidth: 160}}>
                                        <InputLabel id="xml-example-status">{t('xmlExamples.fields.status')}</InputLabel>
                                        <Select
                                            labelId="xml-example-status"
                                            label={t('xmlExamples.fields.status')}
                                            value={form.status}
                                            onChange={(e) => updateField('status', e.target.value as XmlExampleStatus)}
                                        >
                                            <MenuItem value={XmlExampleStatus.Draft}>{t('xmlExamples.status.draft')}</MenuItem>
                                            <MenuItem value={XmlExampleStatus.Approved}>{t('xmlExamples.status.approved')}</MenuItem>
                                            <MenuItem value={XmlExampleStatus.Deprecated}>{t('xmlExamples.status.deprecated')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <TextField
                                    label={t('xmlExamples.fields.notes')}
                                    value={form.notes}
                                    onChange={(e) => updateField('notes', e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    helperText={t('xmlExamples.fields.notesHelper')}
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Snippets */}
                    <Box>
                        <Stack direction="row" spacing={2} sx={{alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
                            <Box>
                                <Typography variant="h6">
                                    {t('xmlExamples.edit.snippets')} ({form.snippets.length})
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('xmlExamples.edit.snippetsHelper')}
                                </Typography>
                            </Box>
                            <Button startIcon={<Add/>} onClick={addSnippet}>
                                {t('xmlExamples.edit.addSnippet')}
                            </Button>
                        </Stack>

                        {form.snippets.length === 0 && (
                            <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
                                <Typography color="text.secondary" sx={{mb: 2}}>
                                    {t('xmlExamples.edit.noSnippets')}
                                </Typography>
                                <Button variant="outlined" startIcon={<Add/>} onClick={addSnippet}>
                                    {t('xmlExamples.edit.addFirstSnippet')}
                                </Button>
                            </Paper>
                        )}

                        <Stack spacing={2}>
                            {form.snippets.map((snippet, index) => (
                                <Card key={snippet.localKey} variant="outlined">
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                                <Typography variant="subtitle1" sx={{fontWeight: 600, flex: 1}}>
                                                    {t('xmlExamples.edit.snippetNumber', {index: index + 1})}
                                                </Typography>
                                                {snippet.serverId && (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={`ID: ${snippet.serverId.slice(0, 8)}…`}
                                                        sx={{fontFamily: 'monospace'}}
                                                    />
                                                )}
                                                <Tooltip title={t('xmlExamples.edit.removeSnippet')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeSnippet(snippet.localKey)}
                                                    >
                                                        <Delete fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <TextField
                                                label={t('xmlExamples.fields.snippetTitle')}
                                                value={snippet.title}
                                                onChange={(e) => updateSnippet(snippet.localKey, {title: e.target.value})}
                                                required
                                                fullWidth
                                                size="small"
                                                slotProps={{htmlInput: {maxLength: 200}}}
                                            />
                                            <TextField
                                                label={t('xmlExamples.fields.snippetDescription')}
                                                value={snippet.description}
                                                onChange={(e) => updateSnippet(snippet.localKey, {description: e.target.value})}
                                                required
                                                fullWidth
                                                size="small"
                                                multiline
                                                minRows={2}
                                                maxRows={4}
                                            />

                                            <Divider/>

                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                                                    {t('xmlExamples.fields.snippetXml')}
                                                </Typography>
                                                <Box sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                }}>
                                                    <Editor
                                                        height="320px"
                                                        language="xml"
                                                        value={snippet.xmlContent}
                                                        onChange={(value) => updateSnippet(snippet.localKey, {xmlContent: value ?? ''})}
                                                        options={{
                                                            minimap: {enabled: false},
                                                            scrollBeyondLastLine: false,
                                                            automaticLayout: true,
                                                            fontSize: 13,
                                                            wordWrap: 'on',
                                                            lineNumbers: 'on',
                                                            folding: true,
                                                            formatOnPaste: true,
                                                            tabSize: 2,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            )}
        </Box>
    );
};

export default XmlExampleEdit;
